import debounce from "lodash/debounce"

/**
 * From the trigger get the next sibling and then get all nav items inside and set focus to last item
 *
 * @param trigger
 */
const setSubmenuFocusToLastMenuItem = trigger => {
  const menuItems = trigger.nextElementSibling.querySelectorAll("[data-nav-item]")
  if (menuItems.length) {
    const lastElement = menuItems[menuItems.length - 1]
    if (lastElement) {
      lastElement.focus()
    }
  }
}

/**
 * From the trigger get the next sibling and then get all nav items inside and set focus to first item
 *
 * @param trigger
 */
const setSubmenuFocusToFirstMenuItem = trigger => {
  const menuItems = trigger.nextElementSibling.querySelectorAll("[data-nav-item]")
  const firstElement = menuItems[0]

  if (firstElement) {
    firstElement.focus()
  }
}

/**
 * From the trigger get all of the nav items that are belongs to the trigger and set focus to next item
 * from the current active element
 *
 * @param trigger
 */
const setSubmenuFocusToNextMenuItem = trigger => {
  // Current active element
  const current = document.activeElement
  // The trigger hold data attribute contains the id of the submenu it belongs to
  const id = trigger.getAttribute("data-nav-item")
  // Get all of the links within the submenu
  const links = [...document.querySelectorAll(`#${id} [data-nav-item="${id}"]`)]

  // Set next item to focus
  if (current === links[links.length - 1]) {
    links[0].focus()
  } else {
    const index = links.findIndex(l => l === current)
    links[index + 1].focus()
  }
}

/**
 * From the trigger get all of the nav items that are belongs to the trigger and set focus to previous item
 * from the current active element
 *
 * @param trigger
 */
const setSubmenuFocusToPreviousMenuItem = trigger => {
  // Current active element
  const current = document.activeElement
  // The trigger hold data attribute contains the id of the submenu it belongs to
  const id = trigger.getAttribute("data-nav-item")
  // Get all of the links within the submenu
  const links = [...document.querySelectorAll(`#${id} [data-nav-item="${id}"]`)]

  // Set previous item to focus
  if (current === links[0]) {
    links[links.length - 1].focus()
  } else {
    const index = links.findIndex(l => l === current)
    if (index > 0) {
      links[index - 1].focus()
    } else {
      links[index].focus()
    }
  }
}

class MobileMenu {
  constructor() {
    this.keyCodes = {
      tab: 9,
      enter: 13,
      space: 32,
      pageup: 33,
      pagedown: 34,
      end: 35,
      home: 36,
      left: 37,
      up: 38,
      right: 39,
      down: 40,
      escape: 27,
    }

    this.bindMenuEvents()
    this.bindEvents()
  }

  /**
   * Events for main menu open and close
   */
  bindMenuEvents() {
    this.siteHeader = document.getElementById("site-header")
    this.mobileMenu = document.getElementById("mobile-menu")
    this.openButton = document.getElementById("open-menu")
    this.closeButton = document.getElementById("close-menu")
    this.subNavButtons = document.querySelectorAll(".site-header__mobile-subnav-button")

    if (this.openButton) {
      this.openButton.addEventListener("click", ev => {
        ev.preventDefault()
        this.toggleMobileMenu()
      })
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", ev => {
        ev.preventDefault()
        this.toggleMobileMenu()
      })
    }
  }

  /**
   * Events for navigating the menu items
   */
  bindEvents() {
    const menuItemButtons = document.querySelectorAll(".site-header__mobile-menu-nav-items [data-nav-btn]")
    const menuItems = document.querySelectorAll(".site-header__mobile-menu-nav-items [data-nav-item]")

    menuItemButtons.forEach(b => {
      b.addEventListener(
        "click",
        debounce(ev => {
          this.handleSubmenuToggle(ev)
        }, 150)
      )

      b.addEventListener("keydown", ev => {
        this.handleMenuButtonKeydown(ev)
      })
    })

    menuItems.forEach(i => {
      i.addEventListener("keydown", ev => {
        this.handleMenuItemKeydown(ev)
      })
    })
  }

  /**
   * Callback to show or close the menu
   */
  toggleMobileMenu() {
    if (this.siteHeader.classList.contains("mobile-menu-open")) {
      this.siteHeader.classList.remove("mobile-menu-open")
      this.mobileMenu.setAttribute("aria-hidden", "true")
      this.openButton.focus()
      // document.body.classList.remove("noscroll")
      this.removeOverlay()
    } else {
      this.siteHeader.classList.add("mobile-menu-open")
      this.mobileMenu.setAttribute("aria-hidden", "false")
      this.closeButton.focus()
      // document.body.classList.add("noscroll")
      this.addOverlay()
    }
  }

  /**
   * Callback for click events to show/hide items.
   *
   * @param ev
   */
  handleSubmenuToggle(ev) {
    const button = ev.target
    const submenu = document.getElementById(button.getAttribute("aria-controls"))

    if (submenu) {
      if (submenu.classList.contains("nav-item__submenu--open")) {
        // Close submenu
        this.closeSubmenu(button, submenu)
      } else {
        // Open submenu
        this.openSubmenu(button, submenu)
      }
    }
  }

  /**
   * Callback for keyboard events for button elements only
   *
   * @param ev
   */
  handleMenuButtonKeydown(ev) {
    let keepPropagation = false

    // The element that holds the registered event
    const button = ev.currentTarget
    // The submenu the button controls
    const submenu = document.getElementById(button.getAttribute("aria-controls"))

    if (!submenu || ev.keyCode === this.keyCodes.tab) {
      return
    }

    switch (ev.keyCode) {
      // Open/Close submenu
      case this.keyCodes.space:
      case this.keyCodes.enter:
      case this.keyCodes.right:
        if (submenu.classList.contains("nav-item__submenu--open")) {
          // Close submenu
          this.closeSubmenu(button, submenu)
        } else {
          // Open submenu
          this.openSubmenu(button, submenu)
          setSubmenuFocusToFirstMenuItem(button)
        }
        break

      // Close submenu or close the main menu if the event is for first level button and its already closed
      case this.keyCodes.escape:
      case this.keyCodes.left:
        // For first level button and if its already closed then close the menu
        if (!button.getAttribute("data-nav-item") && button.getAttribute("aria-expanded") === "false") {
          this.closeButton.dispatchEvent(new Event("click"))
        } else {
          this.closeOpenSubMenu(submenu)
        }
        break
    }

    if (!keepPropagation) {
      ev.stopPropagation()
      ev.preventDefault()
    }
  }

  /**
   * Callback for keyboard events for nav items only
   *
   * @param ev
   */
  handleMenuItemKeydown(ev) {
    let keepPropagation = false

    if (
      ev.ctrlKey ||
      ev.altKey ||
      ev.metaKey ||
      ev.keyCode === this.keyCodes.space ||
      ev.keyCode === this.keyCodes.tab ||
      ev.keyCode === this.keyCodes.enter
    ) {
      return
    }

    // The id of the submenu the nav item belongs to
    const submenuId = ev.currentTarget.getAttribute("data-nav-item")
    // Instance of the submenu the nav item belongs to
    const submenu = document.getElementById(submenuId)
    if (!submenu) {
      return
    }
    // The trigger button that controls the submenu
    const button = document.querySelector(`[aria-controls="${submenu.getAttribute("id")}"]`)

    switch (ev.keyCode) {
      // Focus previous nav item
      case this.keyCodes.up:
        setSubmenuFocusToPreviousMenuItem(ev.currentTarget)
        break

      // Focus next nav item
      case this.keyCodes.down:
        setSubmenuFocusToNextMenuItem(ev.currentTarget)
        break

      // Close submenu if exits - nav item that contains submenu
      case this.keyCodes.escape:
      case this.keyCodes.left:
        this.closeSubmenu(button, submenu)
        button.focus()
        break

      // Open submenu - if the nav item contains submenu and focus first child
      case this.keyCodes.right:
        if (ev.currentTarget.getAttribute("aria-haspopup")) {
          const submenu2 = document.getElementById(ev.currentTarget.getAttribute("aria-controls"))

          this.openSubmenu(ev.currentTarget, submenu2)
          setSubmenuFocusToFirstMenuItem(ev.currentTarget)
        }
        break

      // Focus first item in current submenu
      case this.keyCodes.home:
      case this.keyCodes.pageup:
        setSubmenuFocusToFirstMenuItem(button)
        break

      // Focus last item in current submenu
      case this.keyCodes.end:
      case this.keyCodes.pagedown:
        setSubmenuFocusToLastMenuItem(button)
        break
    }

    if (!keepPropagation) {
      ev.stopPropagation()
      ev.preventDefault()
    }
  }

  /**
   * Open submenu
   *
   * @param button
   * @param submenu
   */
  openSubmenu(button, submenu) {
    button.setAttribute("aria-expanded", "true")
    button.setAttribute("aria-label", button.getAttribute("aria-label").replace(/^Open/, "Close"))
    button.parentNode.classList.add("active")

    submenu.classList.add("nav-item__submenu--open")
    submenu.setAttribute("aria-hidden", "false")
  }

  /**
   * Close submenu
   *
   * @param button
   * @param submenu
   */
  closeSubmenu(button, submenu) {
    button.setAttribute("aria-expanded", "false")
    button.setAttribute("aria-label", button.getAttribute("aria-label").replace(/^Close/, "Open"))
    button.parentNode.classList.remove("active")

    submenu.classList.remove("nav-item__submenu--open")
    submenu.setAttribute("aria-hidden", "true")

    button.focus()
  }

  /**
   * Close opened submenu by submenu instance
   *
   * @param submenu
   */
  closeOpenSubMenu(submenu) {
    const btn = submenu.parentNode.querySelector("button")

    btn.setAttribute("aria-expanded", "false")
    btn.setAttribute("aria-label", btn.getAttribute("aria-label").replace(/^Close/, "Open"))
    btn.parentNode.classList.remove("active")

    submenu.classList.remove("nav-item__submenu--open")
    submenu.setAttribute("aria-hidden", "true")
  }

  /**
   * Add main menu overlay
   */
  addOverlay() {
    let overlay = document.querySelector(".site-header__mobile-menu-overlay")

    if (!overlay) {
      const body = document.querySelector("body")

      overlay = document.createElement("div")
      overlay.classList.add("site-header__mobile-menu-overlay")
      body.append(overlay)

      overlay.addEventListener("click", () => {
        this.toggleMobileMenu()
      })
    }
  }

  /**
   * Remove main menu overlay
   */
  removeOverlay() {
    let overlay = document.querySelector(".site-header__mobile-menu-overlay")
    if (overlay) {
      overlay.remove()
    }
  }
}

export default MobileMenu
